import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Library, Shuffle, ListOrdered } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import * as Api from '../../api';
import type { QuestionDifficulty, SectionKind, SectionSource, UpdateExamSectionInput } from '../../types';

interface Props {
  kind: SectionKind;
  value: SectionSource | undefined;
  onSave: (source: UpdateExamSectionInput['source']) => Promise<void>;
  disabled?: boolean;
}

type Mode = 'OWN' | 'PINNED' | 'RANDOM';

const splitTags = (text: string) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Where a section's questions come from: its own questions, one pinned library
 * section, or a random pick per candidate from the library (P10).
 */
export function SlotSourceEditor({ kind, value, onSave, disabled }: Props) {
  const { t } = useTranslation();
  const initialMode: Mode = value?.mode === 'RANDOM' ? 'RANDOM' : value?.blockId ? 'PINNED' : 'OWN';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [blockId, setBlockId] = useState(value?.blockId ?? '');
  const [difficulty, setDifficulty] = useState<QuestionDifficulty[]>(value?.rule?.difficulty ?? []);
  const [tags, setTags] = useState((value?.rule?.tags ?? []).join(', '));
  const [excludeTags, setExcludeTags] = useState((value?.rule?.excludeTags ?? []).join(', '));
  const [avoidRepeat, setAvoidRepeat] = useState(value?.rule?.avoidRepeatedTopic ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMode(value?.mode === 'RANDOM' ? 'RANDOM' : value?.blockId ? 'PINNED' : 'OWN');
    setBlockId(value?.blockId ?? '');
  }, [value]);

  const library = useQuery({
    queryKey: ['section-library', 'ready', kind],
    queryFn: async () => (await Api.LibraryList({ kind, status: 'READY' })).data.data,
    enabled: mode !== 'OWN',
  });

  const rule = useMemo(
    () => ({ difficulty, tags: splitTags(tags), excludeTags: splitTags(excludeTags), avoidRepeatedTopic: avoidRepeat }),
    [difficulty, tags, excludeTags, avoidRepeat],
  );
  const matches = useQuery({
    queryKey: ['section-library', 'match', kind, rule],
    queryFn: async () => (await Api.LibraryMatchCount(kind, rule)).data.count,
    enabled: mode === 'RANDOM',
  });

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'OWN') await onSave(null);
      else if (mode === 'PINNED') await onSave({ mode: 'FIXED', blockId });
      else await onSave({ mode: 'RANDOM', rule });
    } finally {
      setSaving(false);
    }
  };

  const options: Array<{ mode: Mode; icon: typeof Library; label: string; help: string }> = [
    { mode: 'OWN', icon: ListOrdered, label: t('exam.source.own'), help: t('exam.source.own_help') },
    { mode: 'PINNED', icon: Library, label: t('exam.source.pinned'), help: t('exam.source.pinned_help') },
    { mode: 'RANDOM', icon: Shuffle, label: t('exam.source.random'), help: t('exam.source.random_help') },
  ];

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label={t('exam.source.title')} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map((o) => (
          <button
            key={o.mode}
            type="button"
            role="radio"
            aria-checked={mode === o.mode}
            disabled={disabled}
            onClick={() => setMode(o.mode)}
            className={cn(
              'rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === o.mode ? 'border-foreground bg-muted' : 'border-border hover:bg-muted/50',
            )}
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <o.icon className="h-4 w-4" aria-hidden /> {o.label}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">{o.help}</span>
          </button>
        ))}
      </div>

      {mode === 'PINNED' && (
        <Select value={blockId || undefined} onValueChange={setBlockId} disabled={disabled}>
          <SelectTrigger className="min-h-10 max-w-md">
            <SelectValue placeholder={library.isLoading ? t('loading') : t('exam.source.pick_block')} />
          </SelectTrigger>
          <SelectContent>
            {(library.data ?? []).map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.code} · {b.title} ({t('exam.library.n_questions', { count: b.questionCount })})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {mode === 'RANDOM' && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <fieldset className="flex flex-wrap items-center gap-2">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('exam.difficulty')}</legend>
            {(['easy', 'medium', 'hard'] as const).map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={difficulty.includes(d)}
                disabled={disabled}
                onClick={() => setDifficulty((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))}
                className={cn('rounded-full border px-3 py-1 text-xs font-medium', difficulty.includes(d) ? 'border-foreground bg-foreground text-background' : 'border-border')}
              >
                {t(`exam.diff.${d}`)}
              </button>
            ))}
            <span className="text-xs text-muted-foreground">{t('exam.source.any_if_none')}</span>
          </fieldset>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('exam.source.tags')}</span>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="education, science" disabled={disabled} />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('exam.source.exclude_tags')}</span>
              <Input value={excludeTags} onChange={(e) => setExcludeTags(e.target.value)} placeholder="practice" disabled={disabled} />
            </label>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>
              <span className="font-medium">{t('exam.source.avoid_topic')}</span>
              <span className="block text-xs text-muted-foreground">{t('exam.source.avoid_topic_help')}</span>
            </span>
            <Switch checked={avoidRepeat} onChange={setAvoidRepeat} disabled={disabled} />
          </label>
          <p className={cn('text-sm font-medium', matches.data === 0 ? 'text-destructive' : 'text-foreground')} aria-live="polite">
            {matches.isLoading ? t('loading') : t('exam.source.matches', { count: matches.data ?? 0 })}
          </p>
        </div>
      )}

      <Button size="sm" onClick={save} disabled={disabled || saving || (mode === 'PINNED' && !blockId)}>
        {saving ? t('saving') : t('exam.source.save')}
      </Button>
    </div>
  );
}

export default SlotSourceEditor;
